import { useState, useEffect, useRef } from 'react'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { toPng } from 'html-to-image'
import { useSearchParams, Link } from 'react-router-dom'
import {
  streamAgent, type AgentReport, type AgentInput, type AgentSseEvent,
  type CityTier, type DistancePref, type SchoolType, type TuitionBudget,
  type CareerDirection, type MajorHotPref, type BaoyanIntent, type Adjustment,
  type BodyLimit, type StrongSubject,
  ApiError, translateError, createPreRegistration, trackEvent,
} from '../lib/api'
import { useAuth } from '../lib/store'
import ReportHistory from '../components/ReportHistory'

type DebugEntry =
  | { kind: 'progress'; step: number; total: number; message: string }
  | { kind: 'tool'; tool: string; args: unknown }
  | { kind: 'agent_msg'; turn: number; text: string }
import { SUBJECTS } from '../lib/subjects'

function dotStyle(i: number): React.CSSProperties {
  return {
    width: 8,
    height: 8,
    borderRadius: '50%',
    backgroundColor: 'var(--ink3)',
    animation: 'pulse 1.2s ease-in-out infinite',
    animationDelay: `${i * 0.18}s`,
  }
}

const CITY_TIER_LABEL: Record<CityTier, string> = {
  tier1: '一线(北上广深)',
  newtier1: '新一线',
  tier2: '二线',
  unlimited: '不限',
}
const DISTANCE_LABEL: Record<DistancePref, string> = {
  home_province: '本省',
  neighbor: '邻省',
  must_tier1: '必须北上广深',
  must_south: '必须南方',
  must_north: '必须北方',
  unlimited: '不限',
}
const SCHOOL_TYPE_LABEL: Record<SchoolType, string> = {
  public: '公办',
  private: '民办',
  sino_foreign: '中外合作',
  independent: '独立学院',
  remote_campus: '异地校区',
}
const TUITION_LABEL: Record<TuitionBudget, string> = {
  unlimited: '不限',
  under_5k: '公办(<5千)',
  '5k_15k': '一般(5千-1.5万)',
  '15k_30k': '民办(1.5万-3万)',
  over_30k: '中外合作(>3万)',
}
const CAREER_LABEL: Record<CareerDirection, string> = {
  job: '直接就业',
  graduate_school: '考研',
  abroad: '出国',
  civil_service: '考公',
  entrepreneur: '创业',
  undecided: '还没想好',
}
const HOT_LABEL: Record<MajorHotPref, string> = {
  hot: '热门(AI/计算机等)',
  moderate: '适中',
  cold: '冷门(生化环材等)',
  unlimited: '都可以',
}
const BAOYAN_LABEL: Record<BaoyanIntent, string> = {
  must: '必须保研',
  prefer: '优先保研',
  no_preference: '不在意',
  not_needed: '不需要',
}
const ADJUST_LABEL: Record<Adjustment, string> = {
  comply: '服从调剂',
  no_comply: '不服从',
  depends: '视情况',
}
const BODY_LABEL: Record<BodyLimit, string> = {
  vision: '视力',
  color_blind: '色觉',
  height: '身高',
  hearing: '听力',
}
const SUBJECT_LABEL: Record<StrongSubject, string> = {
  math: '数学', physics: '物理', chemistry: '化学', biology: '生物',
  history: '历史', politics: '政治', geography: '地理', english: '英语', chinese: '语文',
}

export default function Module3() {
  const { user } = useAuth()
  const [score, setScore] = useState('580')
  const [rank, setRank] = useState('')
  const [curriculum, setC] = useState<AgentInput['curriculum']>('物理类')
  const [subject, setS] = useState('物+化+生')
  const [gender, setGender] = useState<'male' | 'female' | ''>('')
  const [cityTier, setCityTier] = useState<CityTier>('unlimited')
  const [distance, setDistance] = useState<DistancePref>('unlimited')
  const [requirements, setRequirements] = useState('')
  const [schoolTypes, setSchoolTypes] = useState<Set<SchoolType>>(new Set(['public']))
  const [tuition, setTuition] = useState<TuitionBudget>('unlimited')
  const [prefer, setPrefer] = useState('')
  const [strongSubs, setStrongSubs] = useState<Set<StrongSubject>>(new Set())
  const [targetJob, setTargetJob] = useState('')
  const [career, setCareer] = useState<CareerDirection>('undecided')
  const [hotPref, setHotPref] = useState<MajorHotPref>('unlimited')
  const [baoyan, setBaoyan] = useState<BaoyanIntent>('no_preference')
  const [risk, setRisk] = useState<AgentInput['risk']>('balanced')
  const [adjustment, setAdjustment] = useState<Adjustment>('comply')
  const [bodyLimits, setBodyLimits] = useState<Set<BodyLimit>>(new Set())
  const [minMath, setMinMath] = useState('')
  const [minEnglish, setMinEnglish] = useState('')
  const [minChinese, setMinChinese] = useState('')

  const [report, setReport] = useState<AgentReport | null>(null)
  const [err, setErr] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const [preRegSuccess, setPreRegSuccess] = useState(false)
  // 假流式下,中间过程只用于 DebugPanel(默认折叠),不直接展示
  const [debugEntries, setDebugEntries] = useState<DebugEntry[]>([])
  const [debugOpen, setDebugOpen] = useState(false)
  const [historyKey, setHistoryKey] = useState(0)
  const abortRef = useRef<AbortController | null>(null)
  const subs = SUBJECTS[curriculum] || []
  useEffect(() => { if (!subs.includes(subject)) setS(subs[0] || '') }, [curriculum])

  // URL 预填(从其他 tab 跳过来时带参数,例如: ?score=580&curriculum=物理类&subject=物+化+生&prefer_profession=西安交通大学)
  const [urlParams] = useSearchParams()
  useEffect(() => {
    const s = urlParams.get('score')
    if (s && !Number.isNaN(+s)) setScore(s)
    const c = urlParams.get('curriculum')
    if (c === '物理类' || c === '历史类') setC(c)
    const p = urlParams.get('prefer_profession')
    if (p) setPrefer(p)
    // 选科预填:必须落在 SUBJECTS 列表里(老 URL 带 2 科选项会被忽略)
    const sj = urlParams.get('subject')
    if (sj && Object.values(SUBJECTS).some(list => list.includes(sj))) setS(sj)
  }, [])  // eslint-disable-line react-hooks/exhaustive-deps

  function toggleSet<T>(s: Set<T>, v: T, set: (s: Set<T>) => void) {
    const next = new Set(s)
    if (next.has(v)) next.delete(v); else next.add(v)
    set(next)
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault(); setErr(null); setReport(null); setLoading(true); setDebugEntries([]); setDebugOpen(false); setPreRegSuccess(false)
    const ac = new AbortController(); abortRef.current = ac

    // 未登录 → 写预登记表,不再调用 agent
    if (!user) {
      try {
        await createPreRegistration({
          score: +score,
          rank: +rank,
          curriculum, subject,
          gender: gender || undefined,
          city_tier: cityTier,
          distance,
          requirements: requirements || undefined,
          school_types: schoolTypes.size ? Array.from(schoolTypes) : undefined,
          tuition,
          prefer_profession: prefer || undefined,
          strong_subjects: strongSubs.size ? Array.from(strongSubs) : undefined,
          target_job: targetJob || undefined,
          career,
          major_hot_pref: hotPref,
          baoyan,
          risk,
          adjustment,
          body_limits: bodyLimits.size ? Array.from(bodyLimits) : undefined,
          min_math: minMath ? +minMath : undefined,
          min_english: minEnglish ? +minEnglish : undefined,
          min_chinese: minChinese ? +minChinese : undefined,
        })
        setPreRegSuccess(true)
      } catch (e) {
        setErr(e instanceof ApiError ? translateError(e) : String((e as Error).message))
      } finally {
        setLoading(false); abortRef.current = null
      }
      return
    }

    const input: AgentInput = {
      score: +score,
      rank: +rank,
      curriculum, subject,
      gender: gender || undefined,
      city_tier: cityTier,
      distance,
      requirements: requirements || undefined,
      school_types: schoolTypes.size ? Array.from(schoolTypes) : undefined,
      tuition,
      prefer_profession: prefer || undefined,
      strong_subjects: strongSubs.size ? Array.from(strongSubs) : undefined,
      target_job: targetJob || undefined,
      career,
      major_hot_pref: hotPref,
      baoyan,
      risk,
      adjustment,
      body_limits: bodyLimits.size ? Array.from(bodyLimits) : undefined,
      min_math: minMath ? +minMath : undefined,
      min_english: minEnglish ? +minEnglish : undefined,
      min_chinese: minChinese ? +minChinese : undefined,
    }
    try {
      await streamAgent(input, (ev: AgentSseEvent) => {
        if (ev.type === 'progress') {
          setDebugEntries(d => [...d, { kind: 'progress', step: ev.step, total: ev.total, message: ev.message }])
        } else if (ev.type === 'tool') {
          setDebugEntries(d => [...d, { kind: 'tool', tool: ev.tool, args: ev.args }])
        } else if (ev.type === 'agent_msg') {
          // DebugPanel: 同 turn 合并,每 turn 一行(不再每字一行)
          setDebugEntries(d => {
            const last = d[d.length - 1]
            if (last && last.kind === 'agent_msg' && last.turn === ev.turn) {
              return [...d.slice(0, -1), { kind: 'agent_msg', turn: ev.turn, text: last.text + ev.text }]
            }
            return [...d, { kind: 'agent_msg', turn: ev.turn, text: ev.text }]
          })
          } else if (ev.type === 'report') {
            setReport(ev.report)
            setHistoryKey(k => k + 1)
            // 埋点:用户成功拿到志愿报告(fire-and-forget)
            trackEvent({ event_type: 'report_include' }).catch(() => {})
          }
        else if (ev.type === 'error') setErr(ev.error)
      })
    } catch (e) {
      setErr(e instanceof ApiError ? translateError(e) : String((e as Error).message))
    } finally {
      setLoading(false); abortRef.current = null
    }
  }

  function populateForm(input: AgentInput) {
    setScore(String(input.score))
    setRank(String(input.rank))
    setC(input.curriculum)
    setS(input.subject)
    setGender(input.gender ?? '')
    setCityTier(input.city_tier ?? 'unlimited')
    setDistance(input.distance ?? 'unlimited')
    setRequirements(input.requirements ?? '')
    setSchoolTypes(new Set(input.school_types ?? ['public']))
    setTuition(input.tuition ?? 'unlimited')
    setPrefer(input.prefer_profession ?? '')
    setStrongSubs(new Set(input.strong_subjects ?? []))
    setTargetJob(input.target_job ?? '')
    setCareer(input.career ?? 'undecided')
    setHotPref(input.major_hot_pref ?? 'unlimited')
    setBaoyan(input.baoyan ?? 'no_preference')
    setRisk(input.risk ?? 'balanced')
    setAdjustment(input.adjustment ?? 'comply')
    setBodyLimits(new Set(input.body_limits ?? []))
    setMinMath(input.min_math ? String(input.min_math) : '')
    setMinEnglish(input.min_english ? String(input.min_english) : '')
    setMinChinese(input.min_chinese ? String(input.min_chinese) : '')
  }

  return (
    <div>
      <h2 className="serif" style={{ fontSize: 'var(--fs-h1)', fontWeight: 600, lineHeight: 1.15, margin: '0 0 .5rem' }}><span className="module-badge">03</span>AI 智能志愿</h2>
      <p style={{ color: 'var(--ink2)', fontSize: 'var(--fs-body)', marginBottom: '1.25rem' }}>填表 30 秒,AI 分析 5 年录取数据,给你冲 / 稳 / 保 3 档建议。每天 5 次。</p>

      {user && <ReportHistory onLoad={setReport} onLoadForm={populateForm} refreshKey={historyKey} />}

      <form onSubmit={submit} className="card" style={{ padding: '1rem' }}>
        <FormSection title="基础信息" required>
          <div className="grid gap-3" style={{ gridTemplateColumns: 'repeat(auto-fill,minmax(160px,1fr))' }}>
            <F label="分数 *"><input className="inp" value={score} onChange={e => setScore(e.target.value)} required /></F>
            <F label="位次 *"><input className="inp" value={rank} onChange={e => setRank(e.target.value)} required placeholder="如 122026" /></F>
            <F label="科类 *"><select className="inp" value={curriculum} onChange={e => setC(e.target.value as any)}><option value="物理类">物理类</option><option value="历史类">历史类</option></select></F>
            <F label="选科组合 *"><select className="inp" value={subject} onChange={e => setS(e.target.value)}>{subs.map(v => <option key={v}>{v}</option>)}</select></F>
            <F label="性别(必填)">
              <select className="inp" value={gender} onChange={e => setGender(e.target.value as any)} required>
                <option value="">请选择</option>
                <option value="male">男</option>
                <option value="female">女</option>
              </select>
            </F>
          </div>
        </FormSection>

        <FormSection title="院校偏好">
          <div className="grid gap-3" style={{ gridTemplateColumns: 'repeat(auto-fill,minmax(160px,1fr))' }}>
            <F label="城市偏好">
              <select className="inp" value={cityTier} onChange={e => setCityTier(e.target.value as CityTier)}>
                {Object.entries(CITY_TIER_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            </F>
            <F label="离家距离">
              <select className="inp" value={distance} onChange={e => setDistance(e.target.value as DistancePref)}>
                {Object.entries(DISTANCE_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            </F>
            <F label="学费预算">
              <select className="inp" value={tuition} onChange={e => setTuition(e.target.value as TuitionBudget)}>
                {Object.entries(TUITION_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            </F>
          </div>
          <div style={{ marginTop: '.5rem' }}>
            <div style={{ color: 'var(--ink3)', marginBottom: 4, fontSize: 'var(--fs-capt)' }}>办学类型(可多选)</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '.5rem' }}>
              {Object.entries(SCHOOL_TYPE_LABEL).map(([k, v]) => (
                <label key={k} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 'var(--fs-capt)', cursor: 'pointer' }}>
                  <input type="checkbox" checked={schoolTypes.has(k as SchoolType)} onChange={() => toggleSet(schoolTypes, k as SchoolType, setSchoolTypes)} />
                  {v}
                </label>
              ))}
            </div>
          </div>
          <div style={{ marginTop: '.75rem' }}>
            <F label="其他要求">
              <input className="inp" value={requirements} onChange={e => setRequirements(e.target.value)} placeholder="如：必须本省、想去南方、不接受高学费、需要保研率高..." style={{ width: '100%' }} />
            </F>
          </div>
        </FormSection>

        <FormSection title="填报策略">
          <div className="grid gap-3" style={{ gridTemplateColumns: 'repeat(auto-fill,minmax(160px,1fr))' }}>
            <F label="风险偏好">
              <select className="inp" value={risk} onChange={e => setRisk(e.target.value as any)}>
                <option value="aggressive">激进</option>
                <option value="balanced">均衡</option>
                <option value="conservative">保守</option>
              </select>
            </F>
            <F label="专业调剂 *">
              <select className="inp" value={adjustment} onChange={e => setAdjustment(e.target.value as Adjustment)}>
                {Object.entries(ADJUST_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            </F>
          </div>
        </FormSection>

        <details style={{ marginTop: '.5rem' }}>
          <summary style={{ cursor: 'pointer', fontWeight: 600, padding: '.5rem 0', color: 'var(--ink)' }}>专业与就业(展开更多)</summary>
          <div style={{ paddingTop: '.5rem' }}>
            <F label="专业偏好">
              <input className="inp" value={prefer} onChange={e => setPrefer(e.target.value)} placeholder="计算机类/临床医学" />
            </F>
            <div style={{ marginTop: '.5rem' }}>
              <div style={{ color: 'var(--ink3)', marginBottom: 4, fontSize: 'var(--fs-capt)' }}>强项科目(可多选)</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '.5rem' }}>
                {Object.entries(SUBJECT_LABEL).map(([k, v]) => (
                  <label key={k} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 'var(--fs-capt)', cursor: 'pointer' }}>
                    <input type="checkbox" checked={strongSubs.has(k as StrongSubject)} onChange={() => toggleSet(strongSubs, k as StrongSubject, setStrongSubs)} />
                    {v}
                  </label>
                ))}
              </div>
            </div>
            <div className="grid gap-3" style={{ gridTemplateColumns: 'repeat(auto-fill,minmax(160px,1fr))', marginTop: '.5rem' }}>
              <F label="就业方向">
                <select className="inp" value={career} onChange={e => setCareer(e.target.value as CareerDirection)}>
                  {Object.entries(CAREER_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                </select>
              </F>
              <F label="专业冷热偏好">
                <select className="inp" value={hotPref} onChange={e => setHotPref(e.target.value as MajorHotPref)}>
                  {Object.entries(HOT_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                </select>
              </F>
              <F label="是否要保研">
                <select className="inp" value={baoyan} onChange={e => setBaoyan(e.target.value as BaoyanIntent)}>
                  {Object.entries(BAOYAN_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                </select>
              </F>
            </div>
            <div style={{ marginTop: '.5rem' }}>
              <F label="目标职业方向(自由文本)">
                <input className="inp" value={targetJob} onChange={e => setTargetJob(e.target.value)} placeholder="算法工程师 / 临床医生 / 教师 等" />
              </F>
            </div>
          </div>
        </details>

        <details style={{ marginTop: '.5rem' }}>
          <summary style={{ cursor: 'pointer', fontWeight: 600, padding: '.5rem 0', color: 'var(--ink)' }}>身体与单科要求(可选,展开)</summary>
          <div style={{ paddingTop: '.5rem' }}>
            <div style={{ color: 'var(--ink3)', marginBottom: 4, fontSize: 'var(--fs-capt)' }}>体检特殊限制(可多选)</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '.5rem' }}>
              {Object.entries(BODY_LABEL).map(([k, v]) => (
                <label key={k} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 'var(--fs-capt)', cursor: 'pointer' }}>
                  <input type="checkbox" checked={bodyLimits.has(k as BodyLimit)} onChange={() => toggleSet(bodyLimits, k as BodyLimit, setBodyLimits)} />
                  {v}
                </label>
              ))}
            </div>
            <div className="grid gap-3" style={{ gridTemplateColumns: 'repeat(auto-fill,minmax(160px,1fr))', marginTop: '.5rem' }}>
              <F label="数学单科 ≥"><input className="inp" type="number" value={minMath} onChange={e => setMinMath(e.target.value)} placeholder="如 110" /></F>
              <F label="英语单科 ≥"><input className="inp" type="number" value={minEnglish} onChange={e => setMinEnglish(e.target.value)} placeholder="如 120" /></F>
              <F label="语文单科 ≥"><input className="inp" type="number" value={minChinese} onChange={e => setMinChinese(e.target.value)} placeholder="如 105" /></F>
            </div>
          </div>
        </details>

        <div style={{ marginTop: '.75rem', display: 'flex', alignItems: 'center', gap: '.75rem', fontSize: 'var(--fs-capt)', color: 'var(--ink3)' }}>
          {user
            ? <><span className="mono">{user.nickname || user.phone}</span><span>· 每天 5 次,跟手机号绑定防滥用</span></>
            : <span>你还没登录。可以先填表点下面的"试用",我们收到后会联系你开通账号。</span>}
        </div>
        <div className="flex items-center gap-3" style={{ marginTop: '.75rem' }}>
          <button type="submit" disabled={loading} className="btn-ink">
            {loading ? (user ? '分析中…' : '保存中…') : user ? '让 AI 帮我看' : '试用 AI 智能志愿'}
          </button>
          {!user && <Link to="/login" className="btn-ghost no-underline" style={{ fontSize: 'var(--fs-capt)' }}>已有账号?登录</Link>}
        </div>
        {!user && !preRegSuccess && (
          <div className="card" style={{ marginTop: '.5rem', padding: '1rem', background: 'var(--bg-deep)' }}>
            <div style={{ fontWeight: 600, fontSize: 'var(--fs-body)', marginBottom: '.5rem' }}>
              点上面的"试用",我们收到后会联系你开通账号
            </div>
            <p style={{ color: 'var(--ink2)', fontSize: 'var(--fs-capt)', lineHeight: 'var(--lh-body)', margin: '0 0 .75rem' }}>
              目前在小范围测试,先填表试用,确认能帮到你再开通。账号是<strong> 手机号 + 密码 </strong>。
            </p>
            <div className="flex gap-5" style={{ flexWrap: 'wrap', alignItems: 'flex-start' }}>
              <div style={{ textAlign: 'center', flexShrink: 0 }}>
                <img
                  src="/wechat-qr.png"
                  alt="客服微信二维码"
                  style={{ width: 120, height: 120, border: '1px solid var(--rule)', borderRadius: 4, display: 'block', background: '#fff' }}
                  onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none' }}
                />
                <div style={{ fontSize: 'var(--fs-capt)', color: 'var(--ink3)', marginTop: '.25rem' }}>微信加客服</div>
              </div>
              <div style={{ flex: 1, minWidth: 200 }}>
                <div style={{ fontSize: 'var(--fs-capt)', color: 'var(--ink3)', marginBottom: '.25rem' }}>或发邮件</div>
                <div style={{ fontSize: 'var(--fs-body)', color: 'var(--ink)', marginBottom: '.5rem', fontFamily: 'var(--mono)' }}>
                  support@boaoai.cn
                </div>
                <a
                  href="mailto:support@boaoai.cn?subject=高考志愿填报助手%20试用申请&body=您好,%0A%0A我想申请 2026 陕西高考志愿填报助手的 AI 智能志愿试用。%0A%0A您怎么知道我们的:%0A考生姓名:%0A考生手机号:%0A%0A谢谢!"
                  style={{ color: 'var(--accent)', textDecoration: 'underline', fontSize: 'var(--fs-capt)' }}
                >
                  发申请邮件 →
                </a>
                <p style={{ color: 'var(--ink3)', fontSize: 'var(--fs-capt)', marginTop: '.5rem', lineHeight: 1.5, margin: '.5rem 0 0' }}>
                  告诉我们<strong>怎么知道我们的 / 考生姓名 / 考生手机号</strong>。人工处理,一般 1-2 天回复。
                </p>
              </div>
            </div>
          </div>
        )}
        {preRegSuccess && (
          <div className="card" style={{ marginTop: '.5rem', padding: '1rem', background: 'var(--bg-deep)', border: '1px solid var(--accent)' }}>
            <div style={{ fontWeight: 600, fontSize: 'var(--fs-body)', marginBottom: '.5rem', color: 'var(--accent)' }}>
              ✓ 收到啦
            </div>
            <p style={{ color: 'var(--ink2)', fontSize: 'var(--fs-capt)', lineHeight: 'var(--lh-body)', margin: '0 0 .75rem' }}>
              你的基本信息和志愿意向已暂存。我们会尽快联系你开通账号。请通过以下方式联系我们,告知<strong>考生姓名</strong>和<strong>手机号</strong>:
            </p>
            <div className="flex gap-5" style={{ flexWrap: 'wrap', alignItems: 'flex-start' }}>
              <div style={{ textAlign: 'center', flexShrink: 0 }}>
                <img
                  src="/wechat-qr.png"
                  alt="客服微信二维码"
                  style={{ width: 120, height: 120, border: '1px solid var(--rule)', borderRadius: 4, display: 'block', background: '#fff' }}
                  onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none' }}
                />
                <div style={{ fontSize: 'var(--fs-capt)', color: 'var(--ink3)', marginTop: '.25rem' }}>微信加客服</div>
              </div>
              <div style={{ flex: 1, minWidth: 200 }}>
                <div style={{ fontSize: 'var(--fs-capt)', color: 'var(--ink3)', marginBottom: '.25rem' }}>或发邮件</div>
                <div style={{ fontSize: 'var(--fs-body)', color: 'var(--ink)', marginBottom: '.5rem', fontFamily: 'var(--mono)' }}>
                  support@boaoai.cn
                </div>
                <a
                  href="mailto:support@boaoai.cn?subject=高考志愿填报助手%20试用申请&body=您好,%0A%0A我想申请 2026 陕西高考志愿填报助手的 AI 智能志愿试用。%0A%0A您怎么知道我们的:%0A考生姓名:%0A考生手机号:%0A%0A谢谢!"
                  style={{ color: 'var(--accent)', textDecoration: 'underline', fontSize: 'var(--fs-capt)' }}
                >
                  发申请邮件 →
                </a>
              </div>
            </div>
          </div>
        )}
      </form>

      {loading && !report && (
        <div className="card" style={{ marginTop: '.75rem', padding: '1.75rem 1.5rem', textAlign: 'center' }}>
          <div style={{ fontSize: '2.5rem', marginBottom: '.5rem', lineHeight: 1 }}>🤔</div>
          <div style={{ fontWeight: 600, fontSize: 'var(--fs-body)', color: 'var(--ink)', marginBottom: '.375rem' }}>
            AI 正在看 5 年录取数据
          </div>
          <div style={{ color: 'var(--ink3)', fontSize: 'var(--fs-capt)' }}>
            30-60 秒给建议,期间不用守着
          </div>
          <div style={{ marginTop: '1rem', display: 'flex', justifyContent: 'center', gap: '.375rem' }}>
            <span style={dotStyle(0)} />
            <span style={dotStyle(1)} />
            <span style={dotStyle(2)} />
          </div>
        </div>
      )}

      {debugEntries.length > 0 && (
        <DebugPanel
          entries={debugEntries}
          open={debugOpen}
          onToggle={() => setDebugOpen(o => !o)}
        />
      )}

      {err && <div className="alert-err" style={{ marginTop: '.75rem' }}>{err}</div>}
      <div id="report-anchor">
        {report && <ReportView report={report} />}
      </div>
    </div>
  )
}

function FormSection({ title, required, children }: { title: string; required?: boolean; children: React.ReactNode }) {
  return (
    <div style={{ marginBottom: '.75rem' }}>
      <div style={{ fontWeight: 600, fontSize: 'var(--fs-body)', marginBottom: '.375rem', color: 'var(--ink)' }}>
        {title}{required && <span style={{ color: 'var(--accent)', marginLeft: 4 }}>*</span>}
      </div>
      {children}
    </div>
  )
}

function F({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label style={{ fontSize: 'var(--fs-capt)', display: 'block' }}>
      <div style={{ color: 'var(--ink3)', marginBottom: 2 }}>{label}</div>
      <div>{children}</div>
    </label>
  )
}

function ReportView({ report }: { report: AgentReport }) {
  const [downloading, setDownloading] = useState(false)
  const [dlErr, setDlErr] = useState<string | null>(null)
  const reportRef = useRef<HTMLDivElement>(null)
  const { score, rank, curriculum } = report.input

  async function downloadImage() {
    if (!reportRef.current) return
    setDownloading(true); setDlErr(null)
    try {
      const dataUrl = await toPng(reportRef.current, {
        pixelRatio: 2,
        backgroundColor: '#ffffff',
        cacheBust: true,
      })
      const a = document.createElement('a')
      a.href = dataUrl
      a.download = `志愿报告-${new Date().toISOString().slice(0, 10)}.png`
      document.body.appendChild(a); a.click(); document.body.removeChild(a)
    } catch (e) {
      setDlErr(String((e as Error).message))
    } finally {
      setDownloading(false)
    }
  }
  return (
    <div style={{ marginTop: '1.5rem' }}>
      <hr className="ruleS" />
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '.5rem' }}>
        <button
          onClick={downloadImage}
          disabled={downloading}
          className="btn-primary"
          style={{ fontSize: 'var(--fs-body)', padding: '.5rem 1rem' }}
        >
          {downloading ? '⏳ 生成中…' : '🖼️ 下载长图'}
        </button>
      </div>
      {dlErr && (
        <div className="alert-warn" style={{ marginBottom: '.5rem', fontSize: 'var(--fs-capt)' }}>
          长图生成失败:{dlErr}
        </div>
      )}
      {/* 截图区域 */}
      <div ref={reportRef} style={{ backgroundColor: '#ffffff', padding: '32px' }}>
        {/* 前端拼接的固定头 */}
        <h1 className="serif" style={{ fontSize: 'var(--fs-h1)', fontWeight: 600, margin: '0 0 .5rem' }}>
          志愿填报报告 · 陕西 · {curriculum} · 分数{score}{rank ? ` · 位次${rank.toLocaleString()}` : ''}
        </h1>
        <div style={{ color: 'var(--ink2)', fontSize: 'var(--fs-capt)', marginBottom: '1rem', lineHeight: 1.6 }}>
          📌 基于 2021-2025 陕西录取数据。院校分为历史录取分，去年位次为跨年可比指标。<br />
          线差 = 院校分 − 你的分，正数=院校分更高(更难上)，负数=院校分更低(更容易)。去年位次越小=越难上。<br />
          数据来源:陕西招生考试院公开数据 + 院校招生章程，填报前请核对官方信息。
        </div>
        <div style={{ background: '#f8f8f8', padding: '.75rem 1rem', borderRadius: 4, fontSize: 'var(--fs-capt)', color: 'var(--ink2)', marginBottom: '1rem', lineHeight: 1.6 }}>
          <strong>怎么看这几档建议?</strong> 冲 = 去年位次远优于你,值得试但概率小; 小冲 = 略优,有希望; 稳 = 位次接近,匹配度高; 保 = 位次靠后,较稳妥; 垫 = 远靠后,绝对兜底。志愿填报是个人决策,本系统仅作参考。
        </div>
        <div style={{ background: '#fff8e1', padding: '.75rem 1rem', borderRadius: 4, fontSize: 'var(--fs-capt)', color: 'var(--ink2)', marginBottom: '1rem', lineHeight: 1.6 }}>
          <strong>⚠️ 提前批决策提示</strong><br />
          6月25日12:00 提前批+本科批同步开放 → 提前批6月27日12:00截止 → 本科批6月30日12:00截止。<br />
          提前批<strong>先录取</strong>，未录取自动滑入本科批；<strong>一旦录取，本科批志愿全部作废</strong>。<br />
          不愿去的学校<strong>空着别填</strong>，把机会留给本科批。定向培养军士需军检、政审。
        </div>

        {/* LLM 生成的动态内容(从 一、整体思路 开始) */}
        <div className="md-report">
          <ReactMarkdown remarkPlugins={[remarkGfm]}>{report.markdown}</ReactMarkdown>
        </div>

        {/* 前端拼接的固定尾 */}
        <hr style={{ border: 'none', borderTop: '1px solid var(--rule)', margin: '1.5rem 0 1rem' }} />
        <div style={{ fontSize: 'var(--fs-capt)', color: 'var(--ink3)', lineHeight: 1.6 }}>
          <strong>免责声明:</strong>本报告由 AI 基于历史录取数据生成，仅供参考，不构成录取承诺。实际录取受当年报考人数、试题难度、招生计划等多因素影响。
        </div>
        {report.warnings.length > 0 && (
          <div className="alert-warn" style={{ marginTop: '.75rem' }}>
            <span style={{ fontWeight: 600, display: 'block', marginBottom: 4 }}>校名核对</span>
            <ul style={{ margin: 0, paddingLeft: '1rem' }}>{report.warnings.map((w, i) => <li key={i}>{w}</li>)}</ul>
          </div>
        )}
      </div>
    </div>
  )
}

/** 折叠调试面板: 显示完整 think + tool + observation 链 */
function DebugPanel({ entries, open, onToggle }: { entries: DebugEntry[]; open: boolean; onToggle: () => void }) {
  return (
    <div className="debug-panel">
      <button onClick={onToggle} className="debug-toggle">
        {open ? '▼' : '▶'} 调试面板 ({entries.length} 条事件)
      </button>
      {open && (
        <div className="debug-content">
          {entries.map((e, i) => {
            if (e.kind === 'progress') {
              return <div key={i} className="dbg-row dbg-progress">[{e.step}/{e.total}] {e.message}</div>
            } else if (e.kind === 'tool') {
              return <div key={i} className="dbg-row dbg-tool">🔧 调用工具:<b>{e.tool}</b> · 参数:{JSON.stringify(e.args)}</div>
            } else {
              return <div key={i} className="dbg-row dbg-msg">💭 第{e.turn}轮:{e.text}</div>
            }
          })}
        </div>
      )}
    </div>
  )
}